/**
 * ReceiptViewDialog — read-only viewer for an existing fee payment.
 *
 * Used from the Payments history page to re-open a historic receipt without
 * showing the collect form. Loads the payment + school by id and renders the
 * same `ReceiptCard` style as FeeCollectionDialog, plus Print, Download PDF
 * and WhatsApp share actions.
 */
import React, { useEffect, useState, useCallback } from "react";
import {
  Receipt,
  WhatsappLogo,
  Printer,
  FilePdf,
  Prohibit,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { printPdf } from "@/lib/printPdf";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import ReceiptCard from "@/components/ReceiptCard";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

function publicPdfUrl(payment) {
  if (!payment) return null;
  if (payment.share_pdf_url) return payment.share_pdf_url;
  if (payment.public_slug) {
    return `${API_BASE}/public/r/${payment.public_slug}/pdf`;
  }
  if (payment.id && payment.share_token) {
    return `${API_BASE}/public/receipts/${payment.id}.pdf?t=${payment.share_token}`;
  }
  return null;
}

export default function ReceiptViewDialog({ open, onOpenChange, paymentId, onChanged }) {
  const { user } = useAuth();
  const canVoid = user?.role === "school_admin" || user?.role === "super_admin";
  const [payment, setPayment] = useState(null);
  const [school, setSchool] = useState(null);
  const [parentPhone, setParentPhone] = useState("");
  const [corrections, setCorrections] = useState([]);
  const [showCorrections, setShowCorrections] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidReason, setVoidReason] = useState("");
  const [voiding, setVoiding] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!paymentId) return;
    setLoading(true);
    setError("");
    try {
      const [p, sc] = await Promise.all([
        api.get(`/school/fee-payments/${paymentId}`),
        api.get("/school/me"),
      ]);
      setPayment(p.data);
      setSchool(sc.data);
      try {
        const stu = await api.get(`/school/students/${p.data.student_id}`);
        setParentPhone(stu.data?.parent_phone || "");
      } catch {
        setParentPhone("");
      }
      // Best-effort load corrections (school admin only — teachers cannot)
      if (user?.role === "school_admin") {
        try {
          const c = await api.get(`/school/fee-payments/${paymentId}/corrections`);
          setCorrections(c.data);
        } catch {
          setCorrections([]);
        }
      }
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  }, [paymentId, user]);

  useEffect(() => {
    if (open) {
      setPayment(null);
      setSchool(null);
      setParentPhone("");
      setCorrections([]);
      setShowCorrections(false);
      setVoidReason("");
      load();
    }
  }, [open, load]);

  const print = () => {
    // Phase P1 — Print the backend-generated 1-page PDF via hidden iframe
    // (see /app/frontend/src/lib/printPdf.js for rationale — was `window.
    // print()` which produced 6 A4 pages by rendering the entire DOM).
    const url = publicPdfUrl(payment);
    printPdf(url);
  };

  const downloadPdf = () => {
    const url = publicPdfUrl(payment);
    if (url) window.open(url, "_blank", "noopener");
  };

  const whatsappShare = async (lang = "en") => {
    if (!payment) return;
    let phone = (parentPhone || "").replace(/[^\d+]/g, "");
    let message = "";
    try {
      const r = await api.get(
        `/school/fee-payments/${payment.id}/share-message?lang=${lang}`,
      );
      message = r.data?.message || "";
      phone = (r.data?.parent_phone || phone).replace(/[^\d+]/g, "");
    } catch (e) {
      const pdfUrl = publicPdfUrl(payment);
      message =
        `Receipt ${payment.receipt_no} from ${school?.school_name || "School"}\n` +
        `${payment.head_snapshot}: ${formatINR(payment.amount_paid)}\n` +
        `Mode: ${payment.payment_mode.toUpperCase()}` +
        (payment.txn_ref ? `\nTxn Ref: ${payment.txn_ref}` : "") +
        (pdfUrl ? `\n\nDownload PDF: ${pdfUrl}` : "");
    }
    const text = encodeURIComponent(message);
    const url = phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, "_blank", "noopener");
  };

  const submitVoid = async () => {
    if (!voidReason || voidReason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters");
      return;
    }
    setVoiding(true);
    try {
      await api.post(`/school/fee-payments/${paymentId}/void`, { reason: voidReason.trim() });
      toast.success("Payment voided");
      setVoidOpen(false);
      onChanged?.();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setVoiding(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl print:max-w-none print:shadow-none" data-testid="receipt-view-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Receipt size={20} weight="duotone" />
            Receipt {payment?.receipt_no || ""}
            {payment?.is_voided && (
              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded bg-red-100 text-red-800 text-[11px] font-bold uppercase tracking-wider" data-testid="receipt-voided-badge">
                Voided
              </span>
            )}
            {(payment?.correction_count || 0) > 0 && (
              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded bg-amber-100 text-amber-800 text-[11px] font-bold uppercase tracking-wider">
                Corrected ({payment.correction_count})
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            View, print, download or share this fee receipt.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-slate-500">Loading…</div>
        ) : error ? (
          <div className="py-8 text-center text-sm text-red-600">{error}</div>
        ) : payment && school ? (
          <div className="space-y-4">
            {payment.is_voided && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-xs text-red-800" data-testid="receipt-void-info">
                <div className="font-semibold text-sm">This payment has been voided.</div>
                <div className="mt-1">
                  By <strong>{payment.voided_by_name}</strong>
                  {payment.voided_at && ` on ${new Date(payment.voided_at).toLocaleString()}`}
                </div>
                {payment.void_reason && <div className="mt-1 italic">Reason: {payment.void_reason}</div>}
              </div>
            )}

            <ReceiptCard school={school} payment={payment} />

            {corrections.length > 0 && (
              <div data-testid="receipt-corrections">
                <button
                  type="button"
                  onClick={() => setShowCorrections((v) => !v)}
                  className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1.5"
                  data-testid="receipt-corrections-toggle"
                >
                  <ClockCounterClockwise size={14} />
                  Correction history ({corrections.length}) {showCorrections ? "▲" : "▼"}
                </button>
                {showCorrections && (
                  <div className="mt-2 border border-slate-200 rounded-md divide-y divide-slate-100 max-h-48 overflow-auto text-xs">
                    {corrections.map((c) => (
                      <div key={c.id} className="px-3 py-2">
                        <div className="font-semibold text-slate-800">
                          {c.kind === "void" ? "Voided" : "Edited"} by {c.actor_name}
                          <span className="ml-2 text-slate-400 text-[10px]">
                            {new Date(c.created_at).toLocaleString()}
                          </span>
                        </div>
                        <div className="text-slate-600 italic">{c.reason}</div>
                        {c.kind === "edit" && c.patched_fields && (
                          <div className="text-slate-500 mt-0.5 text-[11px]">
                            Fields: {c.patched_fields.join(", ")}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-wrap gap-2 print:hidden">
              <Button
                variant="outline"
                onClick={downloadPdf}
                className="gap-1.5"
                disabled={payment.is_voided}
                data-testid="receipt-download-pdf-btn"
              >
                <FilePdf size={14} weight="fill" /> Download PDF
              </Button>
              <Button variant="outline" onClick={print} className="gap-1.5" data-testid="receipt-print-btn">
                <Printer size={14} /> Print
              </Button>
              {canVoid && !payment.is_voided && (
                <Button
                  variant="outline"
                  onClick={() => setVoidOpen(true)}
                  className="gap-1.5 border-red-300 text-red-700 hover:bg-red-50 hover:text-red-700"
                  data-testid="receipt-void-btn"
                >
                  <Prohibit size={14} /> Void
                </Button>
              )}
              <div className="flex-1" />
              <Button
                onClick={() => whatsappShare("en")}
                disabled={payment.is_voided}
                className="bg-green-600 hover:bg-green-700 gap-1.5"
                data-testid="receipt-whatsapp-btn"
              >
                <WhatsappLogo size={14} weight="fill" /> Share on WhatsApp
              </Button>
              <Button
                onClick={() => whatsappShare("hi")}
                disabled={payment.is_voided}
                variant="outline"
                className="border-green-600 text-green-700 hover:bg-green-50 hover:text-green-700 gap-1.5 px-2"
                title="Share message in Hindi"
                data-testid="receipt-whatsapp-hi-btn"
              >
                <WhatsappLogo size={14} weight="fill" /> हिंदी
              </Button>
            </div>
          </div>
        ) : null}
      </DialogContent>

      {/* Void confirmation dialog */}
      <AlertDialog open={voidOpen} onOpenChange={setVoidOpen}>
        <AlertDialogContent data-testid="receipt-void-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <Prohibit size={20} weight="duotone" /> Void receipt {payment?.receipt_no}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              The payment record stays in history (for audit) but will <strong>not</strong>{" "}
              count toward totals or balances anymore. This action is reversible only by a Super Admin.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label className="text-xs">Reason (required, ≥ 3 chars)</Label>
            <Input
              value={voidReason}
              onChange={(e) => setVoidReason(e.target.value)}
              placeholder="e.g. Duplicate entry, wrong amount…"
              data-testid="receipt-void-reason"
              autoFocus
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={voiding}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); submitVoid(); }}
              disabled={voiding}
              className="bg-red-700 hover:bg-red-800"
              data-testid="receipt-void-confirm-btn"
            >
              {voiding ? "Voiding…" : "Yes, void"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
}
